"use client";

import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Features() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const pillars = [{
    title: "Precision Engineering",
    body: "We deliver high-performance industrial solutions with meticulous attention to detail. Every component is tested to ensure maximum reliability and operational efficiency in demanding environments.",
    rotation: "md:-rotate-[1.5deg]",
    mobileRotation: "-rotate-[0.75deg]",
    tapeColor: "bg-[hsl(178_62%_70%/0.6)]",
    tapeRotation: "-rotate-6",
    icon: <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-12 h-12 text-primary">
          <circle cx="32" cy="32" r="24" />
          <path d="M32 8 L32 56 M8 32 L56 32" />
          <circle cx="32" cy="32" r="8" fill="hsl(178 62% 70%)" stroke="hsl(178 62% 70%)" />
        </svg>
  }, {
    title: "Certified Quality",
    body: "Our parts meet rigorous international standards for safety and performance. We source only verified materials to guarantee that your machinery operates at peak capacity without unexpected downtime.",
    rotation: "md:rotate-[1deg]",
    mobileRotation: "rotate-[0.75deg]",
    tapeColor: "bg-[hsl(178_62%_70%/0.6)]",
    tapeRotation: "rotate-[8deg]",
    icon: <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-12 h-12 text-primary">
          <path d="M12 22 L32 12 L52 22 L52 42 L32 52 L12 42 Z" />
          <path d="M24 32 L30 38 L40 26" stroke="hsl(178 62% 70%)" strokeWidth="2.2" />
        </svg>
  }, {
    title: "Expert Support",
    body: "Our technical team provides comprehensive guidance for installation and maintenance. We are committed to your success, offering rapid response times and deep industry expertise.",
    rotation: "md:-rotate-[0.5deg]",
    mobileRotation: "-rotate-[0.5deg]",
    tapeColor: "bg-[hsl(178_62%_70%/0.6)]",
    tapeRotation: "-rotate-[10deg]",
    icon: <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-12 h-12 text-primary">
          <path d="M48 52 V44 C48 36 40 32 32 32 C24 32 16 36 16 44 V52" />
          <circle cx="32" cy="20" r="8" />
          <path d="M40 20 L52 20" stroke="hsl(178 62% 70%)" strokeWidth="2.2" />
        </svg>
  }];
  return <section data-section-id="16103" id="key-features-benefits" className="bg-background py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto max-w-7xl px-6 md:px-10">
        <div ref={introRef} className={`max-w-[640px] mx-auto text-center transition-all duration-[600ms] ease-out ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="font-default text-xs uppercase tracking-[0.18em] text-muted-foreground font-medium mb-5">
            Our Commitment
          </p>
          <h2 className="font-default font-bold text-foreground text-4xl md:text-5xl lg:text-[3.25rem] leading-[1.1] tracking-tight">
            Engineered for{" "}
            <span className="relative inline-block">
              <span className="relative z-10">excellence</span>
              <svg viewBox="0 0 120 24" preserveAspectRatio="none" aria-hidden="true" className="absolute left-0 right-0 -bottom-2 w-full h-3 overflow-visible">
                <path d="M4 14 Q22 6, 44 12 T84 11 Q102 10, 116 16" fill="none" stroke="hsl(178 62% 70%)" strokeWidth="2.2" strokeLinecap="round" strokeDasharray="200" strokeDashoffset={introVisible ? "0" : "200"} style={{
                transition: "stroke-dashoffset 900ms ease-out 200ms"
              }} />
              </svg>
            </span>
          </h2>
          <p className="mt-6 font-default text-base md:text-lg text-muted-foreground leading-relaxed">
            {"Core pillars that define our approach to industrial service and component supply."}
          </p>
        </div>

        <div ref={gridRef} className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {pillars.map((pillar, i) => <div key={i} data-index={i} className={`group transition-all duration-[600ms] ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionDelay: gridVisible ? `${i * 80}ms` : "0ms"
        }}>
              <Card className={`relative bg-card border-0 rounded-lg p-7 md:p-8 gap-5 transition-all duration-[320ms] ease-out shadow-md hover:shadow-xl hover:-translate-y-1 ${pillar.mobileRotation} ${pillar.rotation} hover:!rotate-0`}>
                <div className={`absolute -top-3 right-6 md:right-8 w-16 h-5 ${pillar.tapeColor} ${pillar.tapeRotation} rounded-md shadow-sm`} aria-hidden="true" />

                <div className="mb-1">{pillar.icon}</div>

                <h3 className="font-default text-foreground text-xl font-semibold leading-tight tracking-tight">
                  {pillar.title}
                </h3>

                <p className="font-default text-[0.975rem] text-muted-foreground leading-[1.65]">
                  {pillar.body}
                </p>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}